import type React from "react";
import type { StatsNode } from "../../../types/graphStatsTypes";
import { STATS_COLORS, getSuccessRateColor } from "../../../types/graphStatsTypes";

interface StatsNodeAssetProps {
  node: StatsNode;
  dimmed?: boolean;
  onClick?: (node: StatsNode) => void;
  onMouseEnter?: (node: StatsNode, e: React.MouseEvent) => void;
  onMouseLeave?: () => void;
}

// Static dimensions
const WIDTH = 80;
const HEIGHT = 80;

type AssetType = "image" | "video" | "location" | "sound" | "control" | "model3d" | "effect";

// Icon paths for different asset types
const iconPaths: Record<AssetType, JSX.Element | JSX.Element[]> = {
  image: [
    <path
      key="outline"
      d="M26.219 10.079H12.097a2.017 2.017 0 00-2.018 2.017v14.122c0 1.114.903 2.018 2.018 2.018h14.122a2.018 2.018 0 002.017-2.018V12.096a2.018 2.018 0 00-2.017-2.017z"
    />,
    <path
      key="strokes"
      d="M15.627 17.14a1.513 1.513 0 100-3.027 1.513 1.513 0 000 3.026zM25.21 21.048l-3.909-3.908-8.599 8.599"
    />,
  ],
  video: (
    <path d="M26.790713,10 L12.05409,10 C10.9196496,10 10,10.9196496 10,12.05409 L10,26.790713 C10,27.9251783 10.9196496,28.8448609 12.05409,28.8448609 L26.790713,28.8448609 C27.9251783,28.8448609 28.8448609,27.9251783 28.8448609,26.790713 L28.8448609,12.05409 C28.8448609,10.9196496 27.9251783,10 26.790713,10 Z M14.711273,10 L14.711273,28.8448609 M24.1338522,10 L24.1338522,28.8448609 M10,19.4223478 L28.8448609,19.4223478 M10,14.7110748 L14.711207,14.7110748 M10,24.1336043 L14.711207,24.1336043 M24.1338522,24.1336043 L28.8450261,24.1336043 M24.1338522,14.7110748 L28.8450261,14.7110748" />
  ),
  location: (
    <path d="M18.945 9C15.105 9 12 12.075 12 15.877c0 4.097 4.385 9.746 6.19 11.898a.991.991 0 001.519 0c1.796-2.152 6.18-7.801 6.18-11.898C25.89 12.075 22.785 9 18.946 9zm0 9.333a2.493 2.493 0 01-1.754-.72 2.444 2.444 0 01-.726-1.736c0-.651.26-1.276.726-1.737a2.493 2.493 0 011.754-.719c.658 0 1.289.259 1.754.72.465.46.726 1.085.726 1.736 0 .652-.261 1.276-.726 1.737-.465.46-1.096.72-1.754.72z" />
  ),
  sound: (
    <path d="M28,24.4527027 C28,25.1297695 27.7310891,25.7786599 27.252913,26.2565128 C26.8194652,26.6894915 26.2464118,26.9496884 25.6403487,26.9934186 L25.457646,27 L24.535157,27 C23.862303,27 23.216714,26.7328206 22.7397892,26.2564121 C22.3051462,25.8220628 22.043304,25.2462929 21.9992885,24.6365405 L21.9926636,24.4527027 L21.9926636,21.6872547 C21.9926636,21.0102327 22.2616819,20.3613296 22.7398901,19.8834446 C23.1733663,19.4504375 23.7463179,19.1902638 24.3524337,19.1465381 L24.535157,19.1399574 L26.6051911,19.1392096 L26.605248,18.0000563 C26.605248,15.9877531 25.8051471,14.0575298 24.3801863,12.6336998 C22.9538366,11.2084822 21.0184507,10.4074243 19.0000139,10.4074243 C16.9815456,10.4074243 15.0461506,11.2084845 13.6198505,12.6336909 C12.2627453,13.9897677 11.4723725,15.8051345 11.4001762,17.7131975 L11.394752,18.0000563 L11.3940875,19.1392096 L13.4649266,19.1399574 C14.137724,19.1399574 14.7834099,19.4071664 15.2602866,19.8835653 C15.6948919,20.317912 15.9567023,20.8936742 16.0007124,21.5034191 L16.0073364,21.6872547 L16.0073364,24.4527027 C16.0073364,25.1297166 15.7383525,25.7786107 15.2601857,26.2564929 C14.8267532,26.6894912 14.2537255,26.9496885 13.6476381,26.9934186 L13.4649266,27 L12.5424098,27 C11.869601,27 11.2239143,26.7327928 10.7470359,26.2563921 C10.3124334,25.8220483 10.0506322,25.2462875 10.0066238,24.6365393 L10,24.4527027 L10,18.0000563 C10,15.6116352 10.949333,13.3214704 12.6384598,11.6336236 C14.3262818,9.94709588 16.6145051,9 19.0000139,9 C21.3854884,9 23.6736959,9.94709099 25.3615614,11.633617 C26.9803238,13.2510946 27.9196076,15.4217707 27.9950709,17.7020442 L28,18.0000563 L28,24.4527027 Z" />
  ),
  control: (
    <path d="M26.3272326,12.0000001 L11.4420326,12.0000001 C10.2151726,11.9998262 9.00729258,12.3000562 7.92594258,12.8739612 C6.84459258,13.4478612 5.92332258,14.2776312 5.24415258,15.2893912 C4.56498858,16.3011512 4.14899458,17.4635112 4.03321158,18.6729912 C3.91742858,19.8824612 4.10544758,21.1015212 4.58053058,22.2216712 C5.05561258,23.3417712 5.80302258,24.3281712 6.75620258,25.0930712 C7.70939258,25.8579712 8.83878258,26.3775712 10.0438326,26.6056712 C11.2488726,26.8337712 12.4921826,26.7632712 13.6630726,26.4004712 C14.8339326,26.0376712 15.8960326,25.3938712 16.7549326,24.5261712 L21.0111326,24.5261712 C21.8698326,25.3937712 22.9318326,26.0375712 24.1025326,26.4003712 C25.2733326,26.7631712 26.5164326,26.8337712 27.7214326,26.6058712 C28.9263326,26.3778712 30.0556326,25.8584712 31.0088326,25.0937712 C31.9620326,24.3291712 32.7095326,23.3429712 33.1848326,22.2231712 C33.6601326,21.1032412 33.8484326,19.8843612 33.7330326,18.6749912 C33.6175326,17.4656212 33.2019326,16.3032812 32.5232326,15.2914112 C31.8444326,14.2795412 30.9236326,13.4495412 29.8425326,12.8752912 C28.7615326,12.3010432 27.5539326,12.0003582 26.3272326,12.0000001 Z" />
  ),
  model3d: (
    <svg viewBox="-110 -110 800 800">
      <rect width="240" height="240" x="20" y="90" />
      <rect width="240" height="240" x="140" y="30" />
      <path d="M20,90 l120,-60 M260,90 l120,-60 M20,330 l120,-60 M260,330 l120,-60" />
    </svg>
  ),
  effect: (
    <svg
      strokeWidth="1.5"
      viewBox="0 0 24 24"
      xmlns="http://www.w3.org/2000/svg"
      width={28}
      height={28}
      x={4}
      y={5}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z"
      />
    </svg>
  ),
};

const getIcon = (type: AssetType, color: string, isNotVisited: boolean) => {
  const opacity = isNotVisited ? 0.5 : 0.7;

  switch (type) {
    case "image":
    case "video":
      // outline-based icon
      return (
        <g
          strokeLinecap="round"
          strokeLinejoin="round"
          stroke={color}
          strokeWidth={1.5}
          strokeOpacity={opacity}
        >
          {iconPaths[type]}
        </g>
      );
    case "model3d":
      return (
        <g strokeLinecap="round" strokeLinejoin="round" stroke={color} strokeWidth={15}>
          {iconPaths[type]}
        </g>
      );
    case "location":
    case "sound":
    case "control":
    case "effect":
      // shape-based icon
      return (
        <g opacity={opacity} fill={color}>
          {iconPaths[type]}
        </g>
      );
  }
};

const StatsNodeAsset: React.FC<StatsNodeAssetProps> = ({
  node,
  dimmed = false,
  onClick,
  onMouseEnter,
  onMouseLeave,
}) => {
  // Base stroke color - use the success high color for consistency
  const baseStrokeColor = STATS_COLORS.HIGH;

  const isNotVisited = node.visited === false;
  const hasStatsData = node.stats?.successRate != null;

  // Determine stroke color:
  // Priority order:
  // 1. Not visited (session view): gray
  // 2. Has stats data with problem (< 80%): red/yellow based on success rate
  // 3. Has stats data with good score (>= 80%): green
  // 4. No stats data but is scored type: green (waiting for data)
  // 5. No stats data and not scored type: indigo (non-scored interaction)
  let strokeColor: string = baseStrokeColor;

  if (isNotVisited) {
    strokeColor = STATS_COLORS.NOT_VISITED;
  } else if (hasStatsData && node.stats) {
    // Has actual stats - color based on success rate
    if (node.stats.successRate < 80) {
      strokeColor = getSuccessRateColor(node.stats.successRate);
    }
    // else keep green (good score)
  } else if (!node.isScored) {
    // No stats data AND not a scored interaction type -> purple
    strokeColor = STATS_COLORS.NON_SCORED;
  }
  // else: scored type waiting for data -> keep green
  const circleStrokeWidth = 1.2;

  const handleClick = () => {
    if (onClick) onClick(node);
  };

  const handleMouseEnter = (e: React.MouseEvent) => {
    if (onMouseEnter) onMouseEnter(node, e);
  };

  const assetType = node.assetType || "control";

  // When dimmed, make the node barely visible
  const nodeOpacity = dimmed ? 0.15 : isNotVisited ? 0.5 : 1;

  return (
    <svg
      width={WIDTH}
      height={HEIGHT}
      viewBox="0 0 50 50"
      x={node.x}
      y={node.y}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={onMouseLeave}
      className={dimmed ? "cursor-default" : "cursor-pointer"}
      style={{ overflow: "visible" }}
    >
      <g transform="translate(7 7)" fill="none" fillRule="evenodd" opacity={nodeOpacity}>
        <circle
          strokeWidth={circleStrokeWidth}
          fill="#010038"
          cx={19}
          cy={19}
          r={19}
          stroke={strokeColor}
          strokeDasharray={isNotVisited ? "4,2" : undefined}
        />
        {getIcon(assetType, strokeColor, isNotVisited)}
      </g>
    </svg>
  );
};

// Attach static dimensions for layout calculations
const StatsNodeAssetWithDimensions = Object.assign(StatsNodeAsset, {
  WIDTH,
  HEIGHT,
});

export default StatsNodeAssetWithDimensions;
